import React, { useState, useEffect, useCallback } from 'react';
import { BiImport, BiAnalyse } from "react-icons/bi";
import { CiSearch } from "react-icons/ci";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import partiesData from '../data/party';
import stocksData from '../data/stock';
import updatestocksApi from '../data/stockhistory';

// --- TYPES ---
type StockCategoryObj = {
  id: number;
  stock_name: string;
  category: string;
  unit: string;
  total_amount: string;
  indirect_charges: string;
  perUnitCharge: string;
};

interface Party {
  id: number;
  date: string;
  name: string;
  number: string;
  contact_person_name: string;
  contact_person_number: string;
  categories: string[];
}

type PartyStockDBType = {
  id: number | string;
  party_id: number | string;
  categories: string | StockCategoryObj[];
  date?: string;
  date_of_purchase?: string;
};

type StockCurrentAmountRecord = {
  id: number;
  stock_id: number;
  cat_id: number;
  current_price: number | string;
  current_price_date: string;
};

// Helper: Parse category objects from DB column
function parseCategoriesForDb(categories: string | StockCategoryObj[]): StockCategoryObj[] {
  if (Array.isArray(categories)) return categories as StockCategoryObj[];
  if (typeof categories === 'string') {
    try {
      const value = categories.trim();
      if (value.startsWith('[')) {
        const arr = JSON.parse(value);
        if (Array.isArray(arr)) return arr;
        return [arr];
      }
    } catch { }
  }
  return [];
}

// Helper: Get current amount updates (history) for a stock/category
function getAllCurrentAmountsFor(stockId: number | string, catId: number | string, records: StockCurrentAmountRecord[]) {
  if (!stockId || !catId) return [];
  return records
    .filter(record => String(record.stock_id) === String(stockId) && String(record.cat_id) === String(catId))
    .sort((a, b) => (a.current_price_date < b.current_price_date ? 1 : -1));
}

// Helper: Get latest current amount for a stock/category
function getLatestCurrentAmountFor(stockId: number | string, catId: number | string, records: StockCurrentAmountRecord[]) {
  const all = getAllCurrentAmountsFor(stockId, catId, records);
  if (all.length === 0) return null;
  return all[0];
}

function History() {
  const [parties, setParties] = useState<Party[]>([]);
  const [stocks, setStocks] = useState<PartyStockDBType[]>([]);
  const [updates, setUpdates] = useState<StockCurrentAmountRecord[]>([]);
  const [activeTab, setActiveTab] = useState<'party' | 'stock'>('party');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [showDetailsId, setShowDetailsId] = useState<string | number | null>(null);
  const navigate = useNavigate();

  // Fetch all parties
  useEffect(() => {
    async function fetchPartiesApi() {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addparty`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) return setParties([]);
        const data = await res.json();
        if (Array.isArray(data)) {
          setParties(
            data.map((item: any) => ({
              ...item,
              categories: Array.isArray(item.categories)
                ? item.categories
                : (typeof item.categories === "string"
                  ? (
                    item.categories.trim().startsWith("[")
                      ? JSON.parse(item.categories)
                      : item.categories.split(",").map((c: string) => c.trim())
                  ) : [])
            }))
          );
        } else {
          setParties([]);
        }
      } catch {
        setParties([]);
      }
    }
    fetchPartiesApi();
  }, []);

  // Fetch all party/stocks
  const fetchStocks = useCallback(async () => {
    try {
      const res = await stocksData();
      setStocks(Array.isArray(res) ? res : []);
    } catch {
      setStocks([]);
    }
  }, []);

  // Fetch all current amount updates
  const fetchUpdates = useCallback(async () => {
    try {
      const data = await updatestocksApi();
      setUpdates(Array.isArray(data) ? data : []);
    } catch {
      setUpdates([]);
    }
  }, []);

  useEffect(() => {
    fetchStocks();
    fetchUpdates();
  }, [fetchStocks, fetchUpdates]);

  // PARTY AGGREGATION TABLE
  const partyTable = React.useMemo(() => {
    if (!parties.length || !stocks.length) return [];

    // For each party, gather all stock category objects
    return parties.map(party => {
      const relatedStocks = stocks.filter(s => String(s.party_id) === String(party.id));
      const rows: {
        stockId: number | string;
        catId: number | string;
        category: string;
        stock_name: string;
        unit: string;
        total_amount: string;
        openingBalance: number;
        currentAmount: string | number | null;
        currentAmountDate: string | null;
        allUpdates: StockCurrentAmountRecord[];
        date_of_purchase?: string;
      }[] = [];

      relatedStocks.forEach(stock => {
        const cats = parseCategoriesForDb(stock.categories);
        cats.forEach(catObj => {
          const catId = catObj.id ?? '';
          const opening = Number(catObj.total_amount || 0);
          const latestUpdate = getLatestCurrentAmountFor(stock.id, catId, updates);
          rows.push({
            stockId: stock.id,
            catId,
            category: catObj.category,
            stock_name: catObj.stock_name,
            unit: catObj.unit,
            total_amount: catObj.total_amount,
            openingBalance: opening,
            currentAmount: latestUpdate ? latestUpdate.current_price : null,
            currentAmountDate: latestUpdate ? latestUpdate.current_price_date : null,
            allUpdates: getAllCurrentAmountsFor(stock.id, catId, updates),
            date_of_purchase: stock.date_of_purchase || stock.date || '',
          });
        });
      });

      return {
        id: party.id,
        name: party.name,
        rows
      };
    });
  }, [parties, stocks, updates]);

  // STOCK TABLE (aggregation by stock, one row per party/stock/category)
  const stockTable = React.useMemo(() => {
    if (!parties.length || !stocks.length) return [];

    const res: any[] = [];
    stocks.forEach(stock => {
      const party = parties.find(p => String(p.id) === String(stock.party_id));
      if (!party) return;
      const cats = parseCategoriesForDb(stock.categories);
      cats.forEach(catObj => {
        const catId = catObj.id ?? '';
        const opening = Number(catObj.total_amount || 0);
        const latestUpdate = getLatestCurrentAmountFor(stock.id, catId, updates);
        res.push({
          id: `${stock.id}-${catId}`,
          party_id: stock.party_id,
          partyName: party.name,
          stockId: stock.id,
          catId,
          category: catObj.category,
          stock_name: catObj.stock_name,
          unit: catObj.unit,
          dateOfPurchase: stock.date_of_purchase || stock.date || '',
          openingBalance: opening,
          currentAmount: latestUpdate ? latestUpdate.current_price : null,
          currentAmountDate: latestUpdate ? latestUpdate.current_price_date : null,
          allUpdates: getAllCurrentAmountsFor(stock.id, catId, updates)
        });
      });
    });
    return res;
  }, [parties, stocks, updates]);

  // CHOOSE DATA SOURCE
  const tableData = activeTab === "party" ? partyTable : stockTable;

  // FILTERING
  const filteredData = React.useMemo(() => {
    if (!search) return tableData;
    return tableData.filter(item => {
      if (activeTab === "party") {
        return item.name?.toLowerCase().includes(search.toLowerCase());
      } else {
        return (
          (item.partyName && item.partyName.toLowerCase().includes(search.toLowerCase())) ||
          (item.stock_name && item.stock_name.toLowerCase().includes(search.toLowerCase())) ||
          (item.category && item.category.toLowerCase().includes(search.toLowerCase()))
        );
      }
    });
  }, [tableData, search, activeTab]);

  // PAGINATION
  const totalPage = Math.ceil(filteredData.length / perPage) || 1;
  const pageData = filteredData.slice((page - 1) * perPage, page * perPage);

  return (
    <div className='p-6'>
      <div className='w-[100%]'>
        <div className='flex flex-wrap justify-between w-full'>
          <div className='flex items-center gap-4'>
            <h1 className='text-[28px] font-[500]'>History</h1>
            <div className='bg-[#F9F5FF] p-2 rounded-[40px]'>
              <span className='text-[#6941C6] text-[12px]'>{parties.length} vendors</span>
            </div>
          </div>
          <button className='flex items-center gap-1 border-[1px] border-[#D0D5DD] bg-[#fff] py-2 px-4 rounded-[8px]'>
            <BiImport /> Import
          </button>
        </div>
        <span className='text-[#667085] text-[14px]'>Keep track of stocks.</span>
      </div>

      <div className='flex justify-between w-[100%] mt-7'>
        <div className="w-[40%] bg-[#fff] h-[38px] flex items-center rounded-[8px] px-2 border-[#D0D5DD] border-[1px] gap-1">
          <CiSearch className="text-[#8488B7] text-[24px]" />
          <input
            type="text"
            placeholder="Search..."
            className="w-full bg-transparent h-[32px] focus:outline-none"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <div className="flex items-center gap-4">
          {['party', 'stock'].map(tab => (
            <div
              key={tab}
              className={`p-[1px] rounded-[8px] ${activeTab === tab
                ? 'bg-gradient-to-r from-[#1CAC70] to-[#FF9500]'
                : 'bg-[#D0D5DD]'}`}
            >
              <button
                onClick={() => { setActiveTab(tab as 'party' | 'stock'); setPage(1); setShowDetailsId(null); }}
                className="flex items-center gap-1 text-[#344054] bg-white py-2 px-8 rounded-[8px]"
              >
                {tab === 'party' ? 'Party' : 'Stock'}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-full overflow-x-auto border border-gray-200 mt-4">
        <table className="w-full max-w-full border-collapse table-auto">
          <thead className="bg-[#FCFCFD]">
            <tr>
              {activeTab === 'party' && (
                <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">
                  Party Name
                </th>
              )}
              {activeTab === "stock" && (
                <>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">Category</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">Stock Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">Unit</th>
                </>
              )}
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">Date of purchase</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">Opening Balance</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">Current Amount</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">Update Date</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">Analyses</th>
              <th className="px-4 py-3 border-b"></th>
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={activeTab === 'stock' ? 9 : 7} className="text-center py-8 text-gray-400 font-semibold">
                  No data found.
                </td>
              </tr>
            ) : (
              pageData.map(item => {
                if (activeTab === "party") {
                  // Show one row per party, multiple stocks in "details"
                  return (
                    <React.Fragment key={item.id}>
                      <tr className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-sm text-[#101828] border-b whitespace-nowrap">{item.name}</td>
                        <td className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap"></td>
                        <td className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap"></td>
                        <td className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap"></td>
                        <td className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap"></td>
                        <td className="px-4 py-3 border-b whitespace-nowrap">
                          <div className="p-[1px] rounded-[8px] w-[96px] bg-gradient-to-r from-[#1CAC70] to-[#FF9500]">
                            <button
                              className="flex items-center gap-1 w-full bg-white py-2 px-3 rounded-[8px] text-[12px]"
                              onClick={() => navigate(`/party/${item.id}`)}
                            >
                              <BiAnalyse /> Analyse
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b whitespace-nowrap">
                          <button
                            onClick={() => setShowDetailsId(showDetailsId === item.id ? null : item.id)}
                            className="flex items-center gap-2"
                          >
                            View Details
                            {showDetailsId === item.id ? <IoIosArrowUp /> : <IoIosArrowDown />}
                          </button>
                        </td>
                      </tr>
                      {showDetailsId === item.id && (
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm border-b whitespace-nowrap !bg-gray-100" colSpan={9}>
                            <div className="mb-3">
                              <div className="font-semibold mb-1">All Stocks</div>
                              <table className="w-full text-xs mb-2">
                                <thead>
                                  <tr>
                                    <th className="text-left p-1">Category</th>
                                    <th className="text-left p-1">Stock Name</th>
                                    <th className="text-right p-1">Unit</th>
                                    <th className="text-right p-1">Purchased Amount</th>
                                    <th className="text-right p-1">Current Amount</th>
                                    <th className="text-right p-1">Update Date</th>
                                    <th className="text-right p-1">All Current Amount Updates</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {item.rows.length === 0 ? (
                                    <tr>
                                      <td colSpan={7} className="text-center">No Stocks</td>
                                    </tr>
                                  ) : (
                                    item.rows.map((row: any, ridx: number) => (
                                      <tr key={row.stockId + '-' + row.catId}>
                                        <td className="p-1">{row.category}</td>
                                        <td className="p-1">{row.stock_name}</td>
                                        <td className="text-right p-1">{row.unit}</td>
                                        <td className="text-right p-1">{row.openingBalance}</td>
                                        <td className="text-right p-1">{row.currentAmount !== null ? `₹${row.currentAmount}` : "-"}</td>
                                        <td className="text-right p-1">
                                          {row.currentAmountDate
                                            ? new Date(row.currentAmountDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })
                                            : "-"}
                                        </td>
                                        <td className="text-right p-1">
                                          {row.allUpdates && row.allUpdates.length > 0 ? (
                                            <table className="text-[10px] w-full border">
                                              <thead>
                                                <tr>
                                                 
                                                  <th>Amount</th>
                                                  <th>Date</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {row.allUpdates.map((rec: StockCurrentAmountRecord, uidx: number) => (
                                                  <tr key={rec.id}>
                                                  
                                                    <td>₹{rec.current_price}</td>
                                                    <td>{rec.current_price_date ? new Date(rec.current_price_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          ) : (
                                            <span>-</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                } else {
                  // Stock view: one (party,stock,cat) per row
                  return (
                    <React.Fragment key={item.id}>
                      <tr className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-sm text-[#101828] border-b whitespace-nowrap">{item.category}</td>
                        <td className="px-4 py-3 text-sm text-[#101828] border-b whitespace-nowrap">{item.stock_name}</td>
                        <td className="px-4 py-3 text-sm text-[#101828] border-b whitespace-nowrap">{item.unit}</td>
                        <td className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">
                          {item.dateOfPurchase
                            ? new Date(item.dateOfPurchase).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                            : ""}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">{item.openingBalance}</td>
                        <td className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">{item.currentAmount !== null ? `₹${item.currentAmount}` : "-"}</td>
                        <td className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">
                          {item.currentAmountDate
                            ? new Date(item.currentAmountDate).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                            : "-"}
                        </td>
                        <td className="px-4 py-3 border-b whitespace-nowrap">
                          <div className="p-[1px] rounded-[8px] w-[96px] bg-gradient-to-r from-[#1CAC70] to-[#FF9500]">
                            <button
                              className="flex items-center gap-1 w-full bg-white py-2 px-3 rounded-[8px] text-[12px]"
                              onClick={() => navigate(`/party/${item.party_id}`)}
                            >
                              <BiAnalyse /> Analyse
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b whitespace-nowrap">
                          <button
                            onClick={() => setShowDetailsId(showDetailsId === item.id ? null : item.id)}
                            className="flex items-center gap-2"
                          >
                            View Details
                            {showDetailsId === item.id ? <IoIosArrowUp /> : <IoIosArrowDown />}
                          </button>
                        </td>
                      </tr>
                      {showDetailsId === item.id && (
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm border-b whitespace-nowrap !bg-gray-100" colSpan={9}>
                            <div className="mb-3">
                              <div className="font-semibold mb-1">Current Amount Update History</div>
                              {item.allUpdates && item.allUpdates.length > 0 ? (
                                <table className="w-full text-xs mb-2">
                                  <thead>
                                    <tr>
                                      <th className="text-left p-1">ID</th>
                                      <th className="text-right p-1">Current Amount</th>
                                      <th className="text-right p-1">Date</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {item.allUpdates.map((rec: StockCurrentAmountRecord) => (
                                      <tr key={rec.id}>
                                        <td className="p-1">{rec.id}</td>
                                        <td className="text-right p-1">₹{rec.current_price}</td>
                                        <td className="text-right p-1">
                                          {rec.current_price_date
                                            ? new Date(rec.current_price_date).toLocaleDateString('en-IN', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                              })
                                            : "-"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <span>No updates found</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                }
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-4">
        <span className="text-[#344054] text-[14px]">
          Page {page} of {totalPage}
        </span>
        <div className='flex gap-2'>
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            className="px-3 py-1 bg-[#fff] text-[#344054] border rounded-[6px]"
          >
            Previous
          </button>
          <button
            disabled={page === totalPage || filteredData.length === 0}
            onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 bg-[#fff] text-[#344054] border rounded-[6px]"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default History;
